import React, { useMemo } from 'react';
import Select from 'react-select';

const TOE_LABELS = {
  direct: 'Direct',
  secondary: 'Secondary',
  aggravation: 'Aggravation',
  presumptive: 'Presumptive',
  increase: 'Increase',
};

const typeLabel = (toe) => TOE_LABELS[toe.toeType] || toe.toeType || 'TOE';

// The type, plus the full title when it says more than the type does
// ("Increase" is both).
const titleOf = (toe) => {
  const title = String(toe.title || '').trim();
  return title && title.toLowerCase() !== typeLabel(toe).toLowerCase() ? title : '';
};

// A plain <select> can't wrap (Nic 2026-11-04, a Claims Manager on every DBQ):
// the longest title (239 characters, the Camp Lejeune presumptive) made
// Chrome's list wider than the screen and scrolled it sideways, so every
// other option read blank. And the type alone listed "Presumptive" four
// times. Each option is the type in bold with its full title wrapped under it.
const formatOptionLabel = (option) => (
  <div style={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
    <div className="fw-semibold">{option.type}</div>
    {option.title ? <div className="small text-muted">{option.title}</div> : null}
  </div>
);

const styles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderColor: state.isFocused ? '#86b7fe' : base.borderColor,
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13, 110, 253, 0.25)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#86b7fe' : base.borderColor },
  }),
  // The chosen one wraps too, so the whole title stays readable.
  singleValue: (base) => ({
    ...base,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    position: 'static',
    transform: 'none',
    maxWidth: '100%',
  }),
  valueContainer: (base) => ({ ...base, paddingTop: 4, paddingBottom: 4 }),
  option: (base) => ({ ...base, paddingTop: 8, paddingBottom: 8 }),
  // Above the Edit DBQ Request modal (Bootstrap's sits at 1055).
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const ToeSelector = ({
  value,
  options,
  onChange,
  disabled,
  placeholder = 'Select Theory of Entitlement',
}) => {
  const choices = useMemo(
    () =>
      (options || []).map((toe) => ({
        value: toe.toeId,
        type: typeLabel(toe),
        title: titleOf(toe),
      })),
    [options],
  );
  const selected = choices.find((c) => c.value === value) || null;

  return (
    <Select
      value={selected}
      options={choices}
      onChange={(opt) => onChange(opt ? opt.value : '')}
      isDisabled={disabled}
      isSearchable={false}
      placeholder={placeholder}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={(o) => `${o.type} ${o.title}`}
      styles={styles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPlacement="auto"
      classNamePrefix="toe-select"
    />
  );
};

export default ToeSelector;
